import { useState } from 'react';
import { Check, Zap, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { SEO, schemaSoftwareApp } from '../components/SEO';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MONTHLY = [
  {
    id: 'recon',
    name: 'RECON',
    price: 149,
    currency: '€',
    period: 'month',
    description: 'For growing fashion brands',
    icon: <Shield className="w-5 h-5" />,
    features: [
      'Full access to talent gallery',
      'Verified fashion professionals across all disciplines',
      'Basic filters: role, seniority, city',
      'Complete candidate profiles',
      'Full pipeline & Kanban management',
      'Bulk add to pipeline',
      'HR dashboard & stats',
    ],
    cta: 'Get RECON',
  },
  {
    id: 'intelligence',
    name: 'INTELLIGENCE',
    price: 349,
    currency: '€',
    period: 'month',
    description: 'For serious hiring teams',
    icon: <Zap className="w-5 h-5" />,
    popular: true,
    features: [
      'Everything in RECON',
      'AI DNA Intelligence Panel',
      'AI Interviewer — concierge chat with candidates',
      'AI Matchmaker — relative role-fit ranking',
      'AI Talent Brief — semantic role search',
      'Advanced filters: country, availability, work mode, experience',
      'Find Similar Profiles',
      'Compare up to 3 candidates',
      'Priority support',
    ],
    cta: 'Get INTELLIGENCE',
  },
];

const ANNUAL = [
  {
    id: 'recon_annual',
    name: 'RECON',
    price: 1252,
    monthlyEquiv: 104,
    originalMonthly: 149,
    currency: '€',
    period: 'year',
    savings: 536,
    description: 'For growing fashion brands',
    icon: <Shield className="w-5 h-5" />,
    features: MONTHLY[0].features,
    cta: 'Get RECON Annual',
  },
  {
    id: 'intelligence_annual',
    name: 'INTELLIGENCE',
    price: 2930,
    monthlyEquiv: 244,
    originalMonthly: 349,
    currency: '€',
    period: 'year',
    savings: 1258,
    description: 'For serious hiring teams',
    icon: <Zap className="w-5 h-5" />,
    popular: true,
    features: MONTHLY[1].features,
    cta: 'Get INTELLIGENCE Annual',
  },
];

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual'
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');

  const successMessage = location.state?.message;
  const plans = billing === 'annual' ? ANNUAL : MONTHLY;

  const handleCheckout = async (planId) => {
    if (!user) { navigate('/register', { state: { from: '/pricing' } }); return; }
    setError('');
    setLoading(planId);
    try {
      const res = await authFetch(`${BACKEND}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, origin: window.location.origin }),
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        // Server returned HTML (gateway/proxy error) instead of JSON
        throw new Error('Payment service is temporarily unavailable. Please try again in a moment.');
      }
      if (!res.ok) throw new Error(data.detail || 'Failed to start checkout. Please try again.');
      if (!data.url) throw new Error('Could not start checkout. Please try again or contact support.');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(null);
    }
  };

  const currentPlan = user?.subscription_plan;
  const isCurrentPlan = (planId) => {
    const base = planId.replace('_annual', '');
    return currentPlan === base;
  };
  // Prevent downgrade: Intelligence users cannot buy RECON
  const PLAN_RANK = { free: 0, recon: 1, intelligence: 2 };
  const isDowngrade = (planId) => {
    if (!user || !currentPlan) return false;
    const targetBase = planId.replace('_annual', '');
    return (PLAN_RANK[targetBase] || 0) < (PLAN_RANK[currentPlan] || 0);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Pricing Plans — RECON & INTELLIGENCE"
        description="Flexible pricing for fashion HR teams. RECON at €149/month for gallery access and pipeline management. INTELLIGENCE at €349/month for AI Matchmaker, advanced analytics and full candidate intelligence."
        path="/pricing"
        schema={schemaSoftwareApp}
      />
      {/* Hero */}
      <section className="py-20 lg:py-24 px-6 lg:px-20 border-b border-border">
        <div className="max-w-[1200px] mx-auto text-center">
          {successMessage && (
            <div className="mb-8 inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm">
              <Check className="w-4 h-4" />{successMessage}
            </div>
          )}
          <h1 className="text-5xl lg:text-6xl font-bold text-foreground mb-4">Simple pricing</h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-10">
            Access Europe's most verified pool of fashion talent. Cancel anytime.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center bg-secondary border border-border p-1 gap-1">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-5 py-2 text-sm font-medium transition-colors ${
                billing === 'monthly' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('annual')}
              className={`px-5 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                billing === 'annual' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual
              <span className="bg-green-600 text-white text-xs px-1.5 py-0.5 font-semibold">
                −30%
              </span>
            </button>
          </div>

          {billing === 'annual' && (
            <p className="text-sm text-green-700 font-medium mt-3">
              Save up to €1,258/year — billed once annually
            </p>
          )}

          {!user && (
            <p className="text-sm text-muted-foreground mt-6">
              Already have an account?{' '}
              <a href="/login" className="text-foreground underline">Sign in</a>
            </p>
          )}
        </div>
      </section>

      {/* Plans */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-[900px] mx-auto">
          {error && (
            <div className="mb-8 p-3 bg-red-50 border border-red-200 text-red-700 text-sm text-center">{error}</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {plans.map((plan) => {
              const current = isCurrentPlan(plan.id);
              const downgrade = isDowngrade(plan.id);
              return (
                <div key={plan.id}
                  className={`border p-8 relative flex flex-col ${
                    downgrade ? 'border-border opacity-60' : plan.popular ? 'border-foreground' : 'border-border'
                  }`}>

                  {plan.popular && !downgrade && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-4 py-1 font-medium">
                      Most Popular
                    </div>
                  )}

                  {/* Annual savings badge */}
                  {billing === 'annual' && !downgrade && (
                    <div className="absolute top-4 right-4 bg-green-600 text-white text-xs px-2 py-0.5 font-semibold">
                      Save €{plan.savings}
                    </div>
                  )}

                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-muted-foreground">{plan.icon}</span>
                    <h2 className="text-xl font-bold tracking-wide">{plan.name}</h2>
                    {current && (
                      <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5">Current Plan</span>
                    )}
                    {downgrade && (
                      <span className="ml-auto text-xs bg-secondary border border-border text-muted-foreground px-2 py-0.5">Included in your plan</span>
                    )}
                  </div>

                  {/* Price display */}
                  <div className="mb-1">
                    <span className="text-5xl font-bold">{plan.currency}{plan.price}</span>
                    <span className="text-muted-foreground text-sm ml-1">/{plan.period}</span>
                  </div>
                  {billing === 'annual' && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-green-700 font-medium">≈ €{plan.monthlyEquiv}/mo</span>
                      <span className="text-xs text-muted-foreground line-through">€{plan.originalMonthly}/mo</span>
                    </div>
                  )}
                  <p className="text-muted-foreground text-sm mb-8">{plan.description}</p>

                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check className="w-4 h-4 text-foreground mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => !current && !downgrade && handleCheckout(plan.id)}
                    disabled={current || downgrade || loading !== null}
                    data-testid={`pricing-cta-${plan.id}`}
                    className={`w-full rounded-none flex items-center justify-center gap-2 py-3 transition-colors ${
                      current
                        ? 'bg-green-600 text-white cursor-default'
                        : downgrade
                        ? 'bg-secondary border border-border text-muted-foreground cursor-not-allowed'
                        : plan.popular
                        ? 'bg-foreground text-background hover:bg-foreground/80'
                        : 'border border-foreground text-foreground bg-white hover:bg-foreground hover:text-background'
                    }`}
                  >
                    {current ? '✓ Active'
                      : downgrade ? 'Already included in INTELLIGENCE'
                      : loading === plan.id ? 'Redirecting...'
                      : !user ? 'Create Account'
                      : <>{plan.cta} <ArrowRight className="w-4 h-4" /></>}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* FAQ / comparison note */}
          <div className="mt-12 border-t border-border pt-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center text-sm text-muted-foreground">
              <div>
                <p className="font-semibold text-foreground mb-1">Cancel anytime</p>
                <p>No lock-in. Cancel with one click from your dashboard.</p>
              </div>
              <div>
                <p className="font-semibold text-foreground mb-1">Secure payments</p>
                <p>Processed by Stripe. We never store card details.</p>
              </div>
              <div>
                <p className="font-semibold text-foreground mb-1">Annual saves 30%</p>
                <p>Pay once, access for a full year. Save up to €1,258.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bespoke / Dedicated Tier */}
      <section className="py-16 px-6 lg:px-20 border-t border-border bg-[hsl(var(--secondary))]">
        <div className="max-w-[900px] mx-auto">
          <div className="border border-[hsl(var(--primary)/0.3)] bg-white p-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-testid="bespoke-tier-card">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2 h-2 bg-[hsl(var(--primary))]" />
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>BESPOKE</span>
              </div>
              <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}>White Glove<br />Talent Search</h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Let our dedicated fashion talent specialists manage your entire search — from briefing to placement. Fully hands-on, fully aligned with your hiring success.
              </p>
              <ul className="space-y-2">
                {[
                  'Dedicated fashion talent specialist assigned to your brief',
                  'Priority sourcing, pre-screening & cultural fit assessment',
                  'Placement guarantee with full flexibility',
                  'Confidential search — candidates never know who is looking',
                  'End-to-end management, from brief to offer',
                ].map(f => (
                  <li key={f} className="flex items-center gap-2.5 text-sm">
                    <Check className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-5">
              <div className="border border-border bg-[hsl(var(--secondary))] p-5 space-y-3">
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>A service crafted around your hire</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Every engagement is individually structured — scope, timeline, and terms are agreed between our team and yours before any commitment is made.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Accepted partners receive a personalised service brief detailing methodology, investment structure, and placement guarantee terms.
                </p>
              </div>
              <Button
                onClick={() => navigate(user ? '/dashboard/dedicated' : '/login')}
                className="w-full rounded-none flex items-center justify-center gap-2 py-3.5 bg-foreground text-background hover:bg-foreground/90"
                data-testid="bespoke-request-cta"
              >
                Request Tailored Service <ArrowRight className="w-4 h-4" />
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Reviewed by our team within 24 hours. No commitment required to enquire.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
